import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { getCurrencySymbolByCurrency } from 'utils';

import { PriceBlock } from 'fronton-react';
import {
  getPrimaryPrice,
  getProductPriceCategory,
  getSecondPrice,
  isWebSellable,
  getPrimaryPriceUnitSlug,
  getAdditionalPriceUnitSlug,
  isLaminateCalculator,
  getIsProjectPriceEnabled,
  getPreviousAdditionalPrice,
  isOutOfStockPriceDisplayed,
} from 'features/product-info/ducks/selectors';
import {
  getLaminateConfigurationArea,
  getLaminatConfigurationPriceCoefficient,
  isLaminateConfigurationCalculator,
} from 'features/configurations/ducks/selectors';
import { getGoodsSupplyCase } from 'features/goods-supply/selectors';
import { isCorrectPriceUnitsForLaminateProjectPrice } from 'features/product-info/utils';
import { RootReducer } from 'store/root-reducer';
import { PriceMainProps } from 'common/components/product-price-view/types';
import { getIsSetPath } from 'common/selectors';
import { PriceViewMappedState, PriceViewProps } from './types';
import { calculatePriceType, concatUnit, getProjectPriceResult, isValues, ratioToPercent } from './utils';
import { PdpProjectBoxCount } from '../pdp-project-box-count';

const BEST_PRICE_TYPE = 'bestPrice';
const BLOCKED_SUPPLY_CASES = ['CASE_1', 'CASE_5'] as const;

export const PriceView: React.FC<
  PriceViewProps & {
    isStickyPanel: boolean;
    secondPriceClassName?: string;
    inBasket: boolean;
    quantity: number;
  }
> = ({
  firstPrice,
  secondPrice,
  isSellable,
  supplyCase,
  productPriceCategory,
  isStickyPanel,
  mainUom,
  additionalUom,
  configurationArea,
  isLaminateCalc,
  isProjectPriceEnabled,
  previousAdditionalPrice,
  inBasket,
  quantity,
  outOfStockPriceDisplayed,
  configurationPriceCoefficient,
  isSetPath,
  ...htmlProps
}) => {
  const currency = getCurrencySymbolByCurrency(firstPrice.currency);
  const isBestPrice = productPriceCategory === BEST_PRICE_TYPE;
  const type = calculatePriceType(isBestPrice, firstPrice.prevPrice, outOfStockPriceDisplayed);

  const isCorrectUomForLaminateProjectPrice = isCorrectPriceUnitsForLaminateProjectPrice({
    mainPriceUnit: mainUom,
    additionalPriceUnit: additionalUom,
  });

  const isShowProjectPrice =
    isProjectPriceEnabled &&
    Boolean(configurationArea) &&
    isCorrectUomForLaminateProjectPrice &&
    isLaminateCalc;

  const { projectPrice, areaPerBox, amountBox } = useMemo(
    () =>
      getProjectPriceResult({
        inBasket,
        quantity,
        mainUom,
        configurationArea,
        previousAdditionalPrice,
        firstPrice,
        secondPrice,
        configurationPriceCoefficient,
      }),
    [
      configurationArea,
      firstPrice,
      inBasket,
      mainUom,
      previousAdditionalPrice,
      quantity,
      secondPrice,
      configurationPriceCoefficient,
    ],
  );

  const renderPrice = (price: number, prevPrice?: string | null, discountPercent?: number | null) => {
    const isSecondPrice = isStickyPanel && (isBestPrice || prevPrice) ? false : true;

    return (
      <PriceBlock
        price={price}
        units={isSetPath || isShowProjectPrice ? undefined : concatUnit(firstPrice)}
        oldPrice={prevPrice ? Number(prevPrice) : undefined}
        oldUnits={currency}
        unitPrice={isSecondPrice && isValues(secondPrice) ? secondPrice.price : undefined}
        unitUnits={isValues(secondPrice) ? concatUnit(secondPrice) : undefined}
        discount={ratioToPercent(discountPercent)}
        type={type}
        size={isStickyPanel ? 'm' : 'xl'}
        {...htmlProps}
      />
    );
  };

  const shouldHidePrice = (): boolean => {
    if (!isSellable) return false;
    const isBlocked =
      supplyCase !== null &&
      BLOCKED_SUPPLY_CASES.includes(supplyCase as (typeof BLOCKED_SUPPLY_CASES)[number]);
    return isBlocked && !outOfStockPriceDisplayed;
  };

  const shouldShowProjectPriceCondition = isSellable && !outOfStockPriceDisplayed && isShowProjectPrice;

  const getPriceContent = (): React.ReactNode => {
    if (shouldHidePrice()) {
      return null;
    }

    if (shouldShowProjectPriceCondition) {
      return (
        <>
          {renderPrice(projectPrice.price, projectPrice.prevPrice, projectPrice.discountPercent)}
          {!isStickyPanel && <PdpProjectBoxCount areaPerBox={areaPerBox} amountBox={amountBox} />}
        </>
      );
    }

    return renderPrice(firstPrice.price, firstPrice.prevPrice, firstPrice.discountPercent);
  };

  const content = getPriceContent();

  return <>{content}</>;
};

export const mapStoreToProps = (store: RootReducer): PriceViewMappedState => {
  const firstPrice = getPrimaryPrice(store) as PriceMainProps;
  const secondPrice = getSecondPrice(store) as PriceMainProps;
  const isSellable = isWebSellable(store);
  const supplyCase = getGoodsSupplyCase(store);
  const productPriceCategory = getProductPriceCategory(store) ?? '';
  const mainUom = getPrimaryPriceUnitSlug(store);
  const additionalUom = getAdditionalPriceUnitSlug(store);
  const configurationArea = getLaminateConfigurationArea(store);
  const configurationPriceCoefficient = getLaminatConfigurationPriceCoefficient(store);
  const isLaminateCalc = isLaminateCalculator(store) || isLaminateConfigurationCalculator(store);
  const isProjectPriceEnabled = getIsProjectPriceEnabled(store);
  const previousAdditionalPrice = getPreviousAdditionalPrice(store);
  const outOfStockPriceDisplayed = isOutOfStockPriceDisplayed(store);
  const isSetPath = getIsSetPath(store);

  return {
    firstPrice,
    secondPrice,
    isSellable,
    supplyCase,
    productPriceCategory,
    mainUom,
    additionalUom,
    configurationArea,
    isLaminateCalc,
    isProjectPriceEnabled,
    previousAdditionalPrice,
    outOfStockPriceDisplayed,
    configurationPriceCoefficient,
    isSetPath,
  };
};

export const PdpPriceView = connect(mapStoreToProps, null, (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...ownProps,
}))(PriceView);
